import { useState } from 'react';
import { apiFetch } from '../utils/streamApi';
import { rememberSos, stopActiveSos } from '../utils/sosState';

const PANIC_REASONS = [
  'Suspicious Person',
  'Break-in / Intrusion',
  'Medical Emergency',
  'Other',
];

// Emergency SOS broadcast — shared by the top navbar. Notifies all registered
// users & command-centre groups immediately.
export default function PanicModal({ onClose }) {
  const [reason,    setReason]    = useState('Suspicious Person');
  const [otherText, setOtherText] = useState('');
  const [sending,   setSending]   = useState(false);
  const [done,      setDone]      = useState(false);
  const [sosId,     setSosId]     = useState('');
  const [stopping,  setStopping]  = useState(false);
  const [stopped,   setStopped]   = useState(false);
  // fetch doesn't throw on an HTTP error, so a failed send / stop used to show
  // "Panic Sent!" / "SOS Stopped" anyway — with the siren still running.
  const [err,       setErr]       = useState('');

  const fire = async () => {
    if (!reason) return;
    const finalReason = reason === 'Other' ? (otherText.trim() || 'Other') : reason;
    setSending(true);
    setErr('');
    try {
      // Flutter WebView injects window.FLUTTER_DEVICE_MODEL (exact make + model).
      // Outside the WebView, fall back to browser UA string parsing.
      let device_model = window.FLUTTER_DEVICE_MODEL || '';
      if (!device_model) {
        const m = navigator.userAgent.match(/;\s*([^;)]+)\s+Build\//i);
        if (m) device_model = m[1].trim();
      }
      const res = await apiFetch('/admin/panic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: finalReason, device_model }),
      });
      const data = res.ok ? await res.json().catch(() => ({})) : {};
      if (!data.sos_id) throw new Error('not sent');
      setSosId(data.sos_id);
      // Kept on the device (PRVSK-122): closing this window must not lose the
      // only way to stop it. The "SOS active" bar offers Stop on every page.
      rememberSos(data.sos_id);
      setDone(true);
    } catch {
      setErr('Panic alert was NOT sent. Check the connection and press Send Alert again.');
      setSending(false);
    }
  };

  const stop = async () => {
    if (!sosId || stopping) return;
    setStopping(true);
    setErr('');
    try {
      const res = await stopActiveSos();
      if (!res.ok) {
        setErr(res.message);
        setStopping(false);
        return;
      }
      setStopped(true);
      setTimeout(onClose, 1500);
    } catch {
      setErr('Could not stop the SOS. Check the connection and try again.');
      setStopping(false);
    }
  };

  const errLine = err && (
    <p role="alert" style={{ fontSize: 13, color: '#ef4444', margin: '0 0 12px', lineHeight: 1.45 }}>{err}</p>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 320, boxShadow: '0 8px 40px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>
        {done ? (
          stopped ? (
            <div style={{ textAlign: 'center', fontSize: 18, color: '#22c55e' }}>✅ SOS Stopped</div>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 18, color: '#ef4444', marginBottom: 16 }}>🆘 Panic Sent!</div>
              <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                Already attended? Stop it to silence the alert on every device.
              </p>
              {errLine}
              <button onClick={stop} disabled={stopping}
                style={{ width: '100%', padding: '10px 0', borderRadius: 6, border: 'none', background: '#22c55e', color: '#fff', fontWeight: 700, cursor: stopping ? 'not-allowed' : 'pointer', opacity: stopping ? 0.6 : 1 }}>
                {stopping ? 'Stopping…' : '✅ Stop SOS'}
              </button>
              <button onClick={onClose} style={{ width: '100%', marginTop: 8, padding: '8px 0', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: 'pointer' }}>
                Hide — SOS stays active
              </button>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '8px 0 0' }}>
                You can stop it any time from the red SOS bar.
              </p>
            </div>
          )
        ) : (
          <>
            <h3 style={{ margin: '0 0 16px', color: '#ef4444' }}>🆘 Panic Alert</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>Select reason — all registered users &amp; CC groups will be notified immediately.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {PANIC_REASONS.map(r => (
                <label key={r} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input type="radio" name="panic_reason" value={r} checked={reason === r} onChange={() => setReason(r)} />
                  {r}
                </label>
              ))}
              {reason === 'Other' && (
                <input
                  autoFocus
                  type="text"
                  placeholder="Describe the reason…"
                  value={otherText}
                  onChange={e => setOtherText(e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 6, border: '1.5px solid #ef4444', fontSize: 13, background: 'var(--surface-alt)', color: 'var(--text)', outline: 'none', width: '100%', boxSizing: 'border-box', marginTop: 4 }}
                />
              )}
            </div>
            {errLine}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={{ flex: 1, padding: '8px 0', borderRadius: 6, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', cursor: 'pointer' }}>Cancel</button>
              <button onClick={fire} disabled={!reason || sending}
                style={{ flex: 1, padding: '8px 0', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 700, cursor: reason && !sending ? 'pointer' : 'not-allowed', opacity: reason && !sending ? 1 : 0.5 }}>
                {sending ? 'Sending…' : '🆘 Send Alert'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
